import { intlLocale } from '@/i18n/dateFormat'
import { addDays, dayKeyOf, diffDays, resolveBusinessTimezone, todayKey } from '@/utils/businessDay'
import type { ChatImage, ChatMember, ChatMessage, LocalImage, LocalMessage } from '../types'

/**
 * 消息列表的纯函数（合并去重、补拉缺口、日期分组、已读、时间文字）。
 * 与 main-mobile src/features/chat/utils/messages.ts 同一套规则；时间按浏览器本地时区显示。
 */

/**
 * Snowflake id 比大小（字符串，不转 Number —— 超出 JS 安全整数会丢精度）。
 * 位数多的大；位数相同按字典序。返回 <0 / 0 / >0。
 */
export function compareIds(a: string, b: string): number {
  if (a.length !== b.length) return a.length - b.length
  return a < b ? -1 : a > b ? 1 : 0
}

/** 客户端消息 id（幂等键）：同一条重发用同一个，后端据此不重复落库。 */
export function newClientMsgId(): string {
  const rand = () => Math.random().toString(16).slice(2, 10).padEnd(8, '0')
  return `m-${Date.now().toString(16)}-${rand()}${rand()}`
}

export function toLocal(message: ChatMessage): LocalMessage {
  return { ...message, status: 'sent' }
}

/**
 * 把新到的消息并进列表（新的在前）。
 *
 * - 同 id 的以新到的为准（去重：实时事件和发送回包可能都带同一条）。
 * - 服务端消息带着某条本地「发送中 / 失败」消息的 clientMsgId 时，替换掉那条本地消息。
 * - 已确认过的 clientMsgId 再来本地状态（上传进度、请求超时判的失败）一律丢掉：
 *   实时事件可能比发送回包先到，晚到的「失败」不能让同一条再冒出来。
 * - 本地未确认的消息排在最前（它们一定比服务端已有的新），之间按本机发送时间排。
 * - 本机发的图片：确认后接着显示本地文件（`local`），之后补拉再拿到同一条也不丢，
 *   不用等预签名 URL 重新下载（不闪）。
 * - 别人发的图片：每次取消息后端都签一个新的预签名 URL；同一张图（同 key）手上的 URL 还没快过期就接着用，
 *   否则轮询 / 补拉每次都换 src、整页图片重新下载（会闪）。`freshImages` = 图片加载失败后点重试：一律用新的。
 */
export function mergeMessages(
  existing: LocalMessage[],
  incoming: LocalMessage[],
  opts: { freshImages?: boolean; now?: number } = {},
): LocalMessage[] {
  const all = [...existing, ...incoming]
  const confirmedClientIds = new Set(
    all.filter((m) => m.status === 'sent' && m.clientMsgId).map((m) => m.clientMsgId as string),
  )
  const localByClientId = new Map<string, LocalImage>()
  for (const m of existing) {
    if (m.local && m.clientMsgId) localByClientId.set(m.clientMsgId, m.local)
  }
  const byId = new Map<string, LocalMessage>()
  const pending: LocalMessage[] = []
  for (const m of all) {
    if (m.status !== 'sent') {
      if (m.clientMsgId && confirmedClientIds.has(m.clientMsgId)) continue
      const at = pending.findIndex((p) => p.clientMsgId === m.clientMsgId)
      if (at >= 0) pending[at] = m
      else pending.push(m)
      continue
    }
    let next = m
    const before = byId.get(m.id)
    if (!opts.freshImages && before?.image && m.image && keepImageUrl(before.image, m.image, opts.now ?? Date.now())) {
      next = { ...next, image: before.image }
    }
    // 视频同理（第五批）：换 src 会让正在播的视频从头重载；文件的下载地址也一样接着用
    if (!opts.freshImages && before?.video && m.video && keepImageUrl(before.video, m.video, opts.now ?? Date.now())) {
      next = { ...next, video: before.video }
    }
    if (!opts.freshImages && before?.file && m.file && keepImageUrl(before.file, m.file, opts.now ?? Date.now())) {
      next = { ...next, file: before.file }
    }
    // 引用块里的缩略图同理：同一张图手上的地址还够用就接着用（否则每次轮询引用缩略图都重新下载、闪）
    const beforeQuote = before?.replyTo?.image
    const quote = m.replyTo?.image
    if (!opts.freshImages && beforeQuote && quote && m.replyTo && keepImageUrl(beforeQuote, quote, opts.now ?? Date.now())) {
      next = { ...next, replyTo: { ...m.replyTo, image: beforeQuote } }
    }
    if (!m.local) {
      const carried = byId.get(m.id)?.local ?? (m.clientMsgId ? localByClientId.get(m.clientMsgId) : undefined)
      if (carried) next = { ...next, local: { uri: carried.uri, width: carried.width, height: carried.height } }
    }
    byId.set(m.id, next)
  }
  const sent = [...byId.values()].sort((x, y) => compareIds(y.id, x.id))
  // 本地未确认的按本机发送时间，新的在前（ISO 时间串可直接比较）
  pending.sort((x, y) => (x.createdAt < y.createdAt ? 1 : x.createdAt > y.createdAt ? -1 : 0))
  return [...pending, ...sent]
}

/** 预签名 URL 离过期还剩不到这么久就换新的 */
const IMAGE_URL_MARGIN_MS = 5 * 60_000

/**
 * S3 SigV4 预签名 URL 的过期时间（X-Amz-Date + X-Amz-Expires）；认不出来返回 null。
 */
export function presignedExpiry(url: string): number | null {
  try {
    const q = new URL(url, 'http://x').searchParams
    const date = q.get('X-Amz-Date')
    const expires = Number(q.get('X-Amz-Expires'))
    const m = date?.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/)
    if (!m || !Number.isFinite(expires) || expires <= 0) return null
    const [y, mo, d, h, mi, s] = m.slice(1).map(Number)
    return Date.UTC(y, mo - 1, d, h, mi, s) + expires * 1000
  } catch {
    return null
  }
}

/** 同一张图、手上的 URL 认得出过期时间且还够用：接着用手上的 */
function keepImageUrl(before: Pick<ChatImage, 'key' | 'url'>, incoming: Pick<ChatImage, 'key' | 'url'>, now: number): boolean {
  if (!before.url || before.key !== incoming.key || before.url === incoming.url) return false
  const expiry = presignedExpiry(before.url)
  return expiry != null && expiry - now > IMAGE_URL_MARGIN_MS
}

function oldestId(messages: LocalMessage[]): string | null {
  let oldest: string | null = null
  for (const m of messages) if (oldest == null || compareIds(m.id, oldest) < 0) oldest = m.id
  return oldest
}

/**
 * 补拉 / 轮询拿到的最新一页，和手上已有的消息之间是不是断了一截
 * （离开期间新消息超过一页：最新一页够不到手上最新的那段）。
 *
 * 断了 = 这页后面还有（hasMore）、这页最旧那条不在手上、而手上又有比它更旧的。
 */
export function latestPageLeavesGap(existing: LocalMessage[], latest: LocalMessage[], hasMore: boolean): boolean {
  if (!hasMore) return false
  const oldest = oldestId(latest)
  if (oldest == null) return false
  const sent = existing.filter((m) => m.status === 'sent')
  if (sent.some((m) => m.id === oldest)) return false
  return sent.some((m) => compareIds(m.id, oldest) < 0)
}

/**
 * 用最新一页替换列表：丢掉比这页最旧那条还旧的已发消息（它们和这页之间有缺口），
 * 本地「发送中 / 失败」的照留。之后往上翻页从这页最旧那条往前翻，把缺的补回来。
 */
export function replaceWithLatestPage(existing: LocalMessage[], latest: LocalMessage[]): LocalMessage[] {
  const oldest = oldestId(latest)
  if (oldest == null) return mergeMessages(existing, latest)
  const kept = existing.filter((m) => m.status !== 'sent' || compareIds(m.id, oldest) >= 0)
  return mergeMessages(kept, latest)
}

/** 列表行：消息，或某一天的分隔条。行按新的在前排，分隔条排在那一天最旧那条之后（显示在它上面）。 */
export type ThreadRow =
  | { kind: 'message'; key: string; message: LocalMessage }
  | { kind: 'day'; key: string; day: string }


/** 用户时区的日期键 YYYY-MM-DD（2026-10-31 起不看浏览器时区） */
export function dayKey(iso: string, tz: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
  return dayKeyOf(d, tz)
}

export function buildThreadRows(messages: LocalMessage[], tz: string): ThreadRow[] {
  const rows: ThreadRow[] = []
  messages.forEach((m, i) => {
    rows.push({ kind: 'message', key: m.clientMsgId && m.status !== 'sent' ? `local-${m.clientMsgId}` : m.id, message: m })
    const older = messages[i + 1]
    const day = dayKey(m.createdAt, tz)
    if (!older || dayKey(older.createdAt, tz) !== day) rows.push({ kind: 'day', key: `day-${day}`, day })
  })
  return rows
}

/**
 * 我发的这条，其他人是不是都读到了（WhatsApp 的蓝色双勾）。
 * 单聊 = 对方读到；群聊 = 其他人全读到。没有其他人时返回 false。
 */
export function isReadByOthers(messageId: string, members: ChatMember[], me: string | null | undefined): boolean {
  const others = members.filter((m) => m.user.id !== me)
  if (others.length === 0) return false
  return others.every((m) => !!m.lastReadMessageId && compareIds(m.lastReadMessageId, messageId) >= 0)
}

function timeText(d: Date, locale: string, tz: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { hour: 'numeric', minute: '2-digit', timeZone: resolveBusinessTimezone(tz) }).format(d)
}

/** 会话列表里的时间：今天 = 时:分，昨天 = Yesterday，一周内 = 星期几，更早 = 日期。 */
export function listTimeLabel(
  iso: string | null | undefined,
  locale: string,
  labels: { yesterday: string },
  tz: string,
  now: Date = new Date(),
): string {
  if (!iso) return ''
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return ''
  const timeZone = resolveBusinessTimezone(tz)
  const ago = diffDays(dayKeyOf(t, tz), todayKey(tz, now))
  if (ago <= 0) return timeText(t, locale, tz)
  if (ago === 1) return labels.yesterday
  if (ago <= 6) return new Intl.DateTimeFormat(intlLocale(locale), { weekday: 'long', timeZone }).format(t)
  return new Intl.DateTimeFormat(intlLocale(locale), { year: 'numeric', month: 'numeric', day: 'numeric', timeZone }).format(t)
}

/** 分隔条上的日期：今天 / 昨天 / 本年内「October 28」/ 更早带年份。day = YYYY-MM-DD（本地日）。 */
export function dayLabel(
  day: string,
  locale: string,
  labels: { today: string; yesterday: string },
  tz: string,
  now: Date = new Date(),
): string {
  const [y, m, d] = day.split('-').map((p) => parseInt(p, 10))
  if (!y || !m || !d) return day
  const today = todayKey(tz, now)
  if (day === today) return labels.today
  if (day === addDays(today, -1)) return labels.yesterday
  // day 是日历日（不是时刻）：取那天 UTC 正午，按 UTC 格式化，任何时区都是同一天
  const date = new Date(`${day}T12:00:00Z`)
  const options: Intl.DateTimeFormatOptions =
    String(y) === today.slice(0, 4) ? { month: 'long', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' }
  return new Intl.DateTimeFormat(intlLocale(locale), { ...options, timeZone: 'UTC' }).format(date)
}

export function bubbleTime(iso: string, locale: string, tz: string): string {
  const t = new Date(iso)
  return Number.isNaN(t.getTime()) ? '' : timeText(t, locale, tz)
}

/** 气泡悬停时显示的完整时间 */
export function fullTime(iso: string, locale: string, tz: string): string {
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return ''
  return new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: 'medium', timeStyle: 'short', timeZone: resolveBusinessTimezone(tz) }).format(t)
}
